import {
  Badge,
  Button,
  Flex,
  Grid,
  Paper,
  Select,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import {
  IconDownload,
  IconFileSearch,
  IconMoneybag,
  IconPackage,
  IconReload,
  IconSearch,
  IconTimeDuration15,
} from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_RowSelectionState,
  MRT_SortingState,
  useMantineReactTable,
} from "mantine-react-table";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useDebouncedCallback, useIsFirstRender } from "@mantine/hooks";
import {
  repositoryDelivery,
  repositoryPos,
} from "../../_base/_const/_constVar";
import _breadcrumb from "../../_base/component/_layout/_breadcrumb";
import { useForm } from "@mantine/form";
import { DatePickerInput } from "@mantine/dates";
import { NotificationExtension } from "../../_base/extension/NotificationExtension";
import { MessageResponse } from "../../model/MessageResponse";
import { BASE_API_DELIVERY } from "../../config";

// Định nghĩa đúng kiểu dữ liệu
type ColumnPinningType = {
  left?: string[];
  right?: string[];
};

const SalesOrderViewV2 = () => {
  const navigate = useNavigate();

  //#region  table

  //data and fetching state
  const [data, setData] = useState<any[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);

  // Thêm state cho tổng số lượng và tổng tiền
  const [totalODAmount, setTotalODAmount] = useState(0);
  const [totalQuantity, setTotalQuantity] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  //table state
  const headerRef = useRef<HTMLDivElement>(null);
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [columnPinning, setColumnPinning] = useState<ColumnPinningType>({});
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  const isFirstRender = useIsFirstRender();

  const form = useForm({
    initialValues: {
      searchText: "",
      fromDate: new Date(new Date().setDate(new Date().getDate() - 30)), // Mặc định 30 ngày trước
      toDate: new Date(), // Mặc định hôm nay
      orderNumber: "",
      returnReason: "",
      orderStatus: "DON_HANG_HOAN_THANH", // Mặc định là đơn hàng hoàn thành
      isPositiveNumber: "false", // Mặc định là số lượng âm
    },
    validate: {
      fromDate: (value) => (!value ? "Vui lòng chọn ngày bắt đầu" : null),
      toDate: (value, values) => {
        if (!value) return "Vui lòng chọn ngày kết thúc";
        if (!values.fromDate) return null;

        const diffTime = Math.abs(value.getTime() - values.fromDate.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays > 30) {
          return "Khoảng thời gian không được vượt quá 30 ngày";
        }

        if (value < values.fromDate) {
          return "Ngày kết thúc phải sau ngày bắt đầu";
        }

        return null;
      },
    },
  });

  // Gộp dữ liệu lý do đổi và trả
  const exchangeReasons = [
    { value: "Đ1", label: "[Đ1]CS đổi mới 100%" },
    { value: "Đ2", label: "[Đ2]Lỗi do nhân viên" },
    { value: "Đ3", label: "[Đ3]Lỗi vận chuyển" },
    { value: "Đ4", label: "[Đ4]Lỗi sản phẩm" },
    { value: "Đ5", label: "[Đ5]Lý do khác" },
  ];

  const returnReasons = [
    { value: "T1", label: "[T1]Trả hàng tính phí" },
    { value: "T2", label: "[T2]Bom hàng" },
    { value: "T3", label: "[T3]Lỗi do nhân viên" },
    { value: "T4", label: "[T4]Lỗi do đối tác vận chuyển" },
    { value: "T5", label: "[T5]Thao tác nhầm(xử lý tại chỗ)" },
    { value: "T6", label: "[T6]KH đổi ý(xử lý tại cửa hàng)" },
    { value: "T7", label: "[T7]Sản phẩm lỗi(xử lý tại chỗ)" },
    { value: "T8", label: "[T8]Lý do khác" },
  ];

  const allReturnReasons = [...exchangeReasons, ...returnReasons];

  // Danh sách trạng thái đơn hàng
  const orderStatusOptions = [
    { value: "", label: "Tất cả trạng thái" },
    { value: "DON_HANG_TAM", label: "ĐƠN HÀNG TẠM" },
    { value: "DON_HANG_CHOT", label: "ĐƠN HÀNG CHỐT" },
    { value: "DON_HANG_DOI_TRA_TAM", label: "ĐƠN HÀNG ĐỔI TRẢ TẠM" },
    { value: "DON_HANG_HOAN_THANH", label: "ĐƠN HÀNG HOÀN THÀNH" },
    { value: "DON_HANG_DOI_TRA", label: "ĐƠN HÀNG ĐỔI TRẢ" },
    {
      value: "THANH_TOAN_CHUA_HOAN_THANH",
      label: "THANH TOÁN CHƯA HOÀN THÀNH",
    },
    { value: "pending", label: "CHỜ XỬ LÝ" },
    { value: "DON_HANG_BAN_TAI_SHOP", label: "ĐƠN HÀNG BÁN TẠI SHOP" },
    { value: "DON_HANG_CHO_DUYET_GIAM", label: "ĐƠN HÀNG CHỜ DUYỆT GIẢM" },
    { value: "DON_HANG_DA_DUYET_GIAM", label: "ĐƠN HÀNG ĐÃ DUYỆT GIẢM" },
    { value: "KHO_XUAT_HANG", label: "KHO XUẤT HÀNG" },
    { value: "DON_HANG_DONG", label: "ĐƠN HÀNG ĐÓNG" },
    { value: "DON_HANG_HUY", label: "ĐƠN HÀNG HỦY" },
  ];

  // Define colors
  const green = { base: "#2e7d32" };
  const olive_green = { light: "#8bc34a", medium: "#689f38" };
  const sky_blue = { medium: "#03a9f4" };

  // Function to get text status
  const getTextStatus = React.useCallback((row: any) => {
    const status = row.original.orderStatus;
    switch (status) {
      case "DON_HANG_TAM":
        return "ĐƠN HÀNG TẠM";
      case "DON_HANG_CHOT":
        return "ĐƠN HÀNG CHỐT";
      case "DON_HANG_DOI_TRA_TAM":
        return "ĐƠN HÀNG ĐỔI TRẢ TẠM";
      case "DON_HANG_HOAN_THANH":
        return "ĐƠN HÀNG HOÀN THÀNH";
      case "DON_HANG_DOI_TRA":
        return "ĐƠN HÀNG ĐỔI TRẢ";
      case "THANH_TOAN_CHUA_HOAN_THANH":
        return "THANH TOÁN CHƯA HOÀN THÀNH";
      case "pending":
        return "CHỜ XỬ LÝ";
      case "DON_HANG_BAN_TAI_SHOP":
        return "ĐƠN HÀNG BÁN TẠI SHOP";
      case "DON_HANG_CHO_DUYET_GIAM":
        return "ĐƠN HÀNG CHỜ DUYỆT GIẢM";
      case "DON_HANG_DA_DUYET_GIAM":
        return "ĐƠN HÀNG ĐÃ DUYỆT GIẢM";
      case "KHO_XUAT_HANG":
        return "KHO XUẤT HÀNG";
      case "DON_HANG_DONG":
        return "ĐƠN HÀNG ĐÓNG";
      case "DON_HANG_HUY":
        return "ĐƠN HÀNG HỦY";
      default:
        return status?.replace(/_/g, " ") || "KHÔNG XÁC ĐỊNH";
    }
  }, []);

  // Function to handle view details
  const handleCheckView = React.useCallback(
    async (row: any) => {
      const url = `/api/v1/CreateSellItem/details?orderNumber=${row.original.orderNumber}`;
      const response = await repositoryPos.get<MessageResponse<any>>(url);
      if (response && response.success) {
        // Kiểm tra nếu orderNumber chứa "DT"
        if (row.original.orderNumber.includes("DT")) {
          navigate("/sell/view-order-return", {
            state: {
              id: row.original.id,
              orderNumber: row.original.orderNumber,
              orderType: row.original.orderType,
              actionType: "view",
              // isReadOnly:
              //   location?.pathname === "/sell/sales-order-list-read-only"
              //     ? true
              //     : false,
            },
          });
        } else {
          navigate("/sell/view-order", {
            state: {
              id: row.original.id,
              orderNumber: row.original.orderNumber,
              orderType: row.original.orderType,
              actionType: "view",
              // isReadOnly:
              //   location?.pathname === "/sell/sales-order-list-read-only"
              //     ? true
              //     : false,
            },
          });
        }
      }
    },
    [navigate]
  );

  // Function to handle export Excel
  const handleExportExcel = async () => {
    // Validate form trước khi export
    const validationResult = form.validate();
    if (validationResult.hasErrors) {
      NotificationExtension.Fails("Vui lòng kiểm tra lại thông tin nhập vào!");
      return;
    }

    // Kiểm tra bắt buộc phải có fromDate và toDate
    if (!form.values.fromDate || !form.values.toDate) {
      NotificationExtension.Fails("Phải chọn ngày bắt đầu và ngày kết thúc!");
      return;
    }

    setIsExporting(true);

    try {
      // Build query parameters giống như fetchData nhưng không có pagination
      const queryParams = new URLSearchParams();

      // Search parameters
      if (form.values.searchText) {
        queryParams.append("KeySearch", form.values.searchText);
      }

      if (form.values.orderNumber) {
        queryParams.append("OrderNumber", form.values.orderNumber);
      }

      if (form.values.returnReason) {
        queryParams.append("ReturnReason", form.values.returnReason);
      }

      // Đặt thời gian bắt đầu ngày (00:00:00) - sử dụng định dạng local
      const fromDateStart = new Date(form.values.fromDate);
      fromDateStart.setHours(0, 0, 0, 0);
      const fromDateString =
        fromDateStart.getFullYear() +
        "-" +
        String(fromDateStart.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(fromDateStart.getDate()).padStart(2, "0") +
        "T00:00:00.000Z";
      queryParams.append("StartDate", fromDateString);

      // Đặt thời gian kết thúc ngày (23:59:59) - sử dụng định dạng local
      const toDateEnd = new Date(form.values.toDate);
      toDateEnd.setHours(23, 59, 59, 999);
      const toDateString =
        toDateEnd.getFullYear() +
        "-" +
        String(toDateEnd.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(toDateEnd.getDate()).padStart(2, "0") +
        "T23:59:59.999Z";
      queryParams.append("EndDate", toDateString);

      if (form.values.isPositiveNumber) {
        queryParams.append("IsPositiveNumber", form.values.isPositiveNumber);
      }

      // Sử dụng orderStatus từ form thay vì cố định
      if (form.values.orderStatus) {
        queryParams.append("Status", form.values.orderStatus);
      }

      // Add sorting if any
      if (sorting.length > 0) {
        const sortParam = sorting
          .map((sort) => `${sort.id}:${sort.desc ? "desc" : "asc"}`)
          .join(",");
        queryParams.append("Sort", sortParam);
      }

      const apiUrl = `/api/v1/OrderHeader/export-returned-order-by-items-report?${queryParams.toString()}`;

      console.log("Export API URL:", apiUrl);

      const _url = BASE_API_DELIVERY + apiUrl;

      // Lấy token từ localStorage
      const token = localStorage.getItem("token");

      // Sử dụng fetch thường
      const response = await fetch(_url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("Export Response:", response);

      if (response.ok) {
        // Lấy binary data từ response
        const blob = await response.blob();

        console.log("Blob size:", blob.size, "bytes");

        // Tạo tên file với timestamp
        const now = new Date();
        const timestamp = `${now.getDate().toString().padStart(2, "0")}${(
          now.getMonth() + 1
        )
          .toString()
          .padStart(2, "0")}${now.getFullYear()}_${now
          .getHours()
          .toString()
          .padStart(2, "0")}${now.getMinutes().toString().padStart(2, "0")}`;
        const fileName = `BaoCaoDoiTraHang_${timestamp}.xlsx`;

        // Tạo URL để download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        NotificationExtension.Success("Xuất file Excel thành công!");
      } else {
        NotificationExtension.Fails(
          "Không thể xuất file Excel - Response không hợp lệ!"
        );
      }
    } catch (error) {
      console.error("Export Error:", error);
      NotificationExtension.Fails("Có lỗi xảy ra khi xuất file Excel!");
    } finally {
      setIsExporting(false);
    }
  };

  // Columns definition - định nghĩa tất cả các cột từ API response
  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "orderNumber",
        header: "Mã đơn hàng",
        enableColumnFilter: false,
        size: 160,
        Cell: ({ renderedCellValue, row }: any) => (
          <Tooltip label="Xem chi tiết">
            <Badge
              radius="sm"
              variant="dot"
              size="md"
              style={{ cursor: "pointer" }}
              color={renderedCellValue === null ? "red" : green.base}
              onDoubleClick={() => handleCheckView(row)}
            >
              {renderedCellValue === null ? "KHÔNG HỢP LỆ" : renderedCellValue}
            </Badge>
          </Tooltip>
        ),
      },
      {
        accessorKey: "attribute19",
        header: "Chứng từ gốc",
        enableColumnFilter: false,
        size: 120,
        Cell: ({ cell }: any) => (
          <Text size="xs" fw={500}>
            {cell.getValue() || "--"}
          </Text>
        ),
      },
      {
        accessorKey: "creationDate",
        header: "Ngày tạo đơn",
        enableColumnFilter: false,
        size: 140,
        Cell: ({ cell }: any) => {
          const dateValue = cell.getValue();
          if (!dateValue) return <Text size="xs">--</Text>;

          const date = new Date(dateValue);
          return (
            <Text size="xs" fw={500}>
              {date.toLocaleDateString("vi-VN", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </Text>
          );
        },
      },
      {
        accessorKey: "orderStatus",
        header: "Trạng thái đơn hàng",
        enableColumnFilter: false,
        size: 200,
        Cell: ({ row }: any) => {
          const orderStatus = row.original.orderStatus;
          let badgeColor;
          switch (orderStatus) {
            case "DON_HANG_TAM":
              badgeColor = "cyan";
              break;
            case "DON_HANG_CHOT":
              badgeColor = "violet";
              break;
            case "DON_HANG_DOI_TRA_TAM":
              badgeColor = "cyan";
              break;
            case "DON_HANG_HOAN_THANH":
              badgeColor = "green";
              break;
            case "DON_HANG_DOI_TRA":
              badgeColor = "green";
              break;
            case "THANH_TOAN_CHUA_HOAN_THANH":
              badgeColor = "red";
              break;
            case "pending":
              badgeColor = "yellow";
              break;
            case "DON_HANG_BAN_TAI_SHOP":
              badgeColor = "green";
              break;
            case "DON_HANG_CHO_DUYET_GIAM":
              badgeColor = olive_green.light;
              break;
            case "DON_HANG_DA_DUYET_GIAM":
              badgeColor = olive_green.medium;
              break;
            case "KHO_XUAT_HANG":
              badgeColor = sky_blue.medium;
              break;
            case "DON_HANG_DONG":
              badgeColor = "red";
              break;
            case "DON_HANG_HUY":
              badgeColor = "gray";
              break;
            default:
              badgeColor = "gray";
          }
          return (
            <Badge
              size="md"
              color={badgeColor}
              style={{
                fontSize: "12.5px",
                fontWeight: "bold",
              }}
              radius="sm"
            >
              {row.original.orderStatus !== null
                ? getTextStatus(row)
                : "Không HỢP LỆ"}
            </Badge>
          );
        },
      },
      {
        accessorKey: "invOrgName",
        header: "Kho/Chi nhánh",
        size: 150,
      },
      {
        accessorKey: "invOrgCode",
        header: "Mã kho",
        size: 80,
        Cell: ({ cell }: any) => (
          <div style={{ textAlign: "center" }}>{cell.getValue()}</div>
        ),
      },
      {
        accessorKey: "deptName",
        header: "Phòng ban",
        size: 180,
      },
      {
        accessorKey: "deptCode",
        header: "Mã phòng ban",
        size: 100,
        Cell: ({ cell }: any) => (
          <div style={{ textAlign: "center" }}>{cell.getValue()}</div>
        ),
      },
      {
        accessorKey: "orgName",
        header: "Đơn vị",
        size: 150,
      },
      {
        accessorKey: "saleName",
        header: "Nhân viên bán hàng",
        size: 160,
      },
      {
        accessorKey: "saleCode",
        header: "Mã NV",
        size: 80,
        Cell: ({ cell }: any) => (
          <div style={{ textAlign: "center" }}>{cell.getValue()}</div>
        ),
      },
      {
        accessorKey: "customerName",
        header: "Khách hàng",
        size: 180,
      },
      {
        accessorKey: "customerPhone",
        header: "SĐT khách hàng",
        size: 120,
      },
      {
        accessorKey: "odItemCode",
        header: "Mã sản phẩm",
        size: 120,
      },
      {
        accessorKey: "odItemName",
        header: "Tên sản phẩm",
        size: 300,
        Cell: ({ cell }: any) => (
          <Tooltip label={cell.getValue()}>
            <Text
              size="xs"
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "280px",
              }}
            >
              {cell.getValue()}
            </Text>
          </Tooltip>
        ),
      },
      {
        accessorKey: "odQuality",
        header: "Số lượng",
        size: 80,
        Cell: ({ cell }: any) => (
          <div
            style={{
              textAlign: "center",
              fontWeight: "bold",
              color: cell.getValue() < 0 ? "#d32f2f" : "#2e7d32",
            }}
          >
            {cell.getValue()}
          </div>
        ),
      },
      {
        accessorKey: "odPriceFinal",
        header: "Đơn giá",
        size: 120,
        Cell: ({ cell }: any) => (
          <div style={{ textAlign: "right" }}>
            {new Intl.NumberFormat("vi-VN", {
              style: "currency",
              currency: "VND",
            }).format(cell.getValue())}
          </div>
        ),
      },
      {
        accessorKey: "odTotalLineAmount",
        header: "Tổng tiền trả lại",
        size: 120,
        Cell: ({ cell }: any) => (
          <div
            style={{ textAlign: "right", fontWeight: "bold", color: "#d32f2f" }}
          >
            {new Intl.NumberFormat("vi-VN", {
              style: "currency",
              currency: "VND",
            }).format(cell.getValue())}
          </div>
        ),
      },
      {
        accessorKey: "odReturnReasonName",
        header: "Lý do đổi/trả",
        size: 150,
        Cell: ({ cell }: any) => (
          <Badge size="sm" color="orange" variant="light">
            {cell.getValue()}
          </Badge>
        ),
      },
      {
        accessorKey: "tenChung",
        header: "Loại chung",
        size: 150,
      },
      {
        accessorKey: "maChung",
        header: "Mã loại chung",
        size: 100,
        Cell: ({ cell }: any) => (
          <div style={{ textAlign: "center" }}>{cell.getValue()}</div>
        ),
      },
      {
        accessorKey: "tenNhom",
        header: "Nhóm sản phẩm",
        size: 160,
      },
      {
        accessorKey: "maNhom",
        header: "Mã nhóm",
        size: 100,
        Cell: ({ cell }: any) => (
          <div style={{ textAlign: "center" }}>{cell.getValue()}</div>
        ),
      },
      {
        accessorKey: "tenLinhVuc",
        header: "Lĩnh vực",
        size: 120,
      },
      {
        accessorKey: "tenNganh",
        header: "Ngành hàng",
        size: 180,
      },
      {
        accessorKey: "tenLoai",
        header: "Loại hàng",
        size: 160,
      },
    ],
    [
      green.base,
      olive_green.light,
      olive_green.medium,
      sky_blue.medium,
      getTextStatus,
      handleCheckView,
    ]
  );

  // Fetch data function
  const fetchData = useDebouncedCallback(async () => {
    // Validate form trước khi gọi API
    const validationResult = form.validate();
    if (validationResult.hasErrors) {
      setIsError(true);
      NotificationExtension.Fails("Vui lòng kiểm tra lại thông tin nhập vào!");
      return;
    }

    // Kiểm tra bắt buộc phải có fromDate và toDate
    if (!form.values.fromDate || !form.values.toDate) {
      setIsError(true);
      NotificationExtension.Fails("Phải chọn ngày bắt đầu và ngày kết thúc!");
      return;
    }

    if (!isFirstRender) {
      setIsError(false);
      setIsRefetching(true);
    }
    setIsLoading(true);

    try {
      // Build query parameters
      const queryParams = new URLSearchParams();

      // Pagination parameters
      queryParams.append("Take", pagination.pageSize.toString());
      queryParams.append(
        "Skip",
        (pagination.pageIndex * pagination.pageSize).toString()
      );

      // Search parameters
      if (form.values.searchText) {
        queryParams.append("KeySearch", form.values.searchText);
      }

      if (form.values.orderNumber) {
        queryParams.append(
          "OrderNumber",
          form.values.orderNumber?.toUpperCase()
        );
      }

      if (form.values.returnReason) {
        queryParams.append("ReturnReason", form.values.returnReason);
      }

      // Đặt thời gian bắt đầu ngày (00:00:00) - sử dụng định dạng local
      const fromDateStart = new Date(form.values.fromDate);
      fromDateStart.setHours(0, 0, 0, 0);
      const fromDateString =
        fromDateStart.getFullYear() +
        "-" +
        String(fromDateStart.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(fromDateStart.getDate()).padStart(2, "0") +
        "T00:00:00.000Z";
      queryParams.append("StartDate", fromDateString);

      // Đặt thời gian kết thúc ngày (23:59:59) - sử dụng định dạng local
      const toDateEnd = new Date(form.values.toDate);
      toDateEnd.setHours(23, 59, 59, 999);
      const toDateString =
        toDateEnd.getFullYear() +
        "-" +
        String(toDateEnd.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(toDateEnd.getDate()).padStart(2, "0") +
        "T23:59:59.999Z";
      queryParams.append("EndDate", toDateString);

      // Không có tham số branchId trong API documentation
      // if (form.values.branchId) {
      //   queryParams.append("branchId", form.values.branchId);
      // }

      if (form.values.isPositiveNumber) {
        queryParams.append("IsPositiveNumber", form.values.isPositiveNumber);
      }

      // Sử dụng orderStatus từ form thay vì cố định
      if (form.values.orderStatus) {
        queryParams.append("Status", form.values.orderStatus);
      }

      // Add sorting if any
      if (sorting.length > 0) {
        const sortParam = sorting
          .map((sort) => `${sort.id}:${sort.desc ? "desc" : "asc"}`)
          .join(",");
        queryParams.append("Sort", sortParam);
      }

      const apiUrl = `/api/v1/OrderHeader/get-list-returned-order-by-items-report?${queryParams.toString()}`;

      // Call API get-list-returned-order-by-items-report
      const response = await repositoryDelivery.get<MessageResponse<any>>(
        apiUrl
      );

      // Set data từ response - cấu trúc mới: data.data.lists
      const responseData = response?.data?.pagedList?.lists || [];
      setData(Array.isArray(responseData) ? responseData : []);

      // Sử dụng totalCount từ cấu trúc mới
      const totalCount =
        response?.data?.pagedList?.count ||
        response?.data?.totalCount ||
        responseData.length ||
        0;
      setRowCount(totalCount);

      // Lấy tổng số lượng và tổng tiền từ API response
      setTotalODAmount(response?.data?.totalODAmount || 0);
      setTotalQuantity(response?.data?.totalQuantity || 0);
    } catch (error) {
      setIsError(true);
      console.error("API Error:", error);
      NotificationExtension.Fails("Có lỗi xảy ra khi tải dữ liệu!");
    }

    setIsError(false);
    setIsLoading(false);
    setIsRefetching(false);
  }, 300);

  useEffect(() => {
    fetchData();
  }, [
    fetchData,
    columnFilters,
    globalFilter,
    pagination.pageIndex,
    pagination.pageSize,
    sorting,
    form.values,
  ]);

  const table = useMantineReactTable({
    columns,
    data,
    enableRowSelection: false,
    getRowId: (row) => row.id,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    onColumnFiltersChange: setColumnFilters,
    onColumnPinningChange: setColumnPinning,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    rowCount,
    state: {
      columnFilters,
      columnPinning,
      globalFilter,
      isLoading,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      sorting,
      rowSelection,
    },
    enableColumnPinning: true,
    positionActionsColumn: "first",
    enableColumnResizing: true,
    enableDensityToggle: false, // Ẩn density toggle
    enableFullScreenToggle: false, // Ẩn fullscreen toggle
    enableGlobalFilter: false, // Ẩn global filter
    enableColumnFilters: false, // Ẩn column filters
    enableSorting: true,
    enableToolbarInternalActions: false, // Ẩn các internal actions
    enableTopToolbar: false, // Ẩn top toolbar
    mantineTableProps: {
      style: {
        fontSize: "13px",
      },
    },
    mantineTableHeadCellProps: {
      style: {
        backgroundColor: "#f8f9fa",
        fontWeight: "bold",
        fontSize: "14px",
      },
    },
    mantineTableBodyCellProps: {
      style: {
        fontSize: "13px",
      },
    },
    mantinePaginationProps: {
      size: "sm",
      showRowsPerPage: true,
      rowsPerPageOptions: ["10", "20", "25", "50", "100"],
    },
    initialState: {
      columnPinning: {
        left: ["orderNumber", "creationDate", "orderStatus"],
      },
      pagination: {
        pageIndex: 0,
        pageSize: 25,
      },
    },
  });

  return (
    <>
      <div
        style={{ border: "1px solid #dee2e6", padding: "10px" }}
        ref={headerRef}
      >
        <Flex
          gap="md"
          justify={"space-between"}
          align="center"
          direction="row"
          wrap="wrap"
          w={"100%"}
        >
          {_breadcrumb()}
          <div>
            <Button
              leftSection={<IconReload size={14} />}
              variant="outline"
              mr={"xs"}
              onClick={() => {
                // Reset về giá trị mặc định thay vì reset hoàn toàn
                form.setValues({
                  searchText: "",
                  fromDate: new Date(
                    new Date().setDate(new Date().getDate() - 30)
                  ),
                  toDate: new Date(),
                  orderNumber: "",
                  returnReason: "",
                  orderStatus: "DON_HANG_HOAN_THANH",
                  isPositiveNumber: "false",
                });
                fetchData();
              }}
            >
              Làm mới
            </Button>
            <Button
              leftSection={<IconDownload size={14} />}
              color="violet"
              variant="outline"
              onClick={handleExportExcel}
              loading={isExporting}
              disabled={isExporting}
            >
              Export Excel
            </Button>
          </div>
        </Flex>

        {/* Search Form */}
        <Grid mt={"10px"}>
          <Grid.Col span={{ base: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
            <TextInput
              leftSection={<IconSearch color="#15aabf" />}
              placeholder="Từ khoá"
              {...form.getInputProps("searchText")}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
            <TextInput
              placeholder="Mã đơn hàng"
              leftSection={<IconFileSearch color="#15aabf" />}
              {...form.getInputProps("orderNumber")}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
            <Select
              placeholder="Lý do đổi/trả"
              data={allReturnReasons}
              {...form.getInputProps("returnReason")}
              leftSection={<IconSearch color="#15aabf" />}
              clearable
              searchable
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
            <Select
              placeholder="Trạng thái đơn hàng"
              data={orderStatusOptions}
              {...form.getInputProps("orderStatus")}
              leftSection={<IconSearch color="#15aabf" />}
              clearable
              searchable
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
            <DatePickerInput
              placeholder="Từ ngày"
              {...form.getInputProps("fromDate")}
              leftSection={<IconTimeDuration15 color="#15aabf" />}
              locale="vi"
              valueFormat="DD/MM/YYYY"
              clearable={false}
              required
              error={form.errors.fromDate}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
            <DatePickerInput
              placeholder="Đến ngày"
              {...form.getInputProps("toDate")}
              leftSection={<IconTimeDuration15 color="#15aabf" />}
              locale="vi"
              valueFormat="DD/MM/YYYY"
              clearable={false}
              required
              error={form.errors.toDate}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
            <Button
              leftSection={<IconSearch size={14} />}
              color="blue"
              variant="outline"
              onClick={() => fetchData()}
              size="sm"
              w={120}
            >
              Tìm kiếm
            </Button>
          </Grid.Col>
        </Grid>

        {/* Summary Cards */}
        <Flex gap="md" mt="md">
          <Paper
            withBorder
            p="sm"
            radius="md"
            style={{
              backgroundColor: "#e3f2fd",
              border: "1px solid #90caf9",
              flex: 1,
            }}
          >
            <Flex align="center" gap="xs">
              <IconPackage size={20} color="#1976d2" />
              <div>
                <Text size="md" fw={600} c="#1976d2">
                  Tổng số lượng trả
                </Text>
                <Text size="xl" fw={700} c="#1976d2">
                  {new Intl.NumberFormat("vi-VN").format(totalQuantity)}
                </Text>
              </div>
            </Flex>
          </Paper>

          <Paper
            withBorder
            p="sm"
            radius="md"
            style={{
              backgroundColor: "#ffebee",
              border: "1px solid #ef9a9a",
              flex: 1,
            }}
          >
            <Flex align="center" gap="xs">
              <IconMoneybag size={20} color="#d32f2f" />
              <div>
                <Text size="md" fw={600} c="#d32f2f">
                  Tổng tiền trả lại
                </Text>
                <Text size="xl" fw={700} c="#d32f2f">
                  {new Intl.NumberFormat("vi-VN", {
                    style: "currency",
                    currency: "VND",
                  }).format(totalODAmount)}
                </Text>
              </div>
            </Flex>
          </Paper>
        </Flex>
      </div>

      <div className="mt-5" style={{ marginTop: "10px" }}>
        <MantineReactTable table={table} />
      </div>
    </>
  );
};

export default SalesOrderViewV2;
